import {
  CruisePurchaseModel,
  CruisePurchaseMutationInput,
} from 'api-hooks/order/cruise';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useRevertVoidAction from 'hooks/use-revert-void-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import OtherPriceComponent from 'legacy-modules/order/component/other-price-component';
import ParentOrderRelatedTable from 'legacy-modules/order/component/parent-children-purchase-table';
import SaleRelatedTable from 'legacy-modules/order/component/sale-related-table';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import {
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import CruiseSection from './cruise';
import { CruisePurchaseFormSchema, CruisePurchaseFormType } from './form-type';
import PurchasesDetailCard from './purchase-detail-card';
import CruisePurchaseSelectInput from './select-input';
import SummaryCruisePurchase from './summary';
import totalCalculation from './total-calculation';
import useGetDefaultValues from '../hooks/use-get-default-values';
import { FormContent, FormDivider } from '../style';

interface Props {
  cruisePurchase?: CruisePurchaseModel;
  onSubmit: (
    input: CruisePurchaseMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<CruisePurchaseModel | undefined>;
  cruiseSale?: CruiseSaleModel;
  renderError?: React.ReactNode;
  defaultValues?: CruisePurchaseFormType;
}

export default function CruisePurchaseForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();

  const { cruisePurchase, renderError, cruiseSale } = props;

  const defaultValues = useGetDefaultValues({
    cruisePurchase,
    cruiseSale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(CruisePurchaseFormSchema());

  const methods = useForm<CruisePurchaseFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: CruisePurchaseFormType) => {
      const {
        cruiseSale,
        cruisePurchases,
        tempTransactionAt,
        agentStatus,
        remainingTransactionQuota,
        isExceedingQuota,
        //remove unused code
        ...rest
      } = values;
      try {
        const total = totalCalculation({
          cruisePurchases: values.cruisePurchases,
          discount: values.discount,
          otherCost: values.otherCost,
        });

        //regenerate cruisePurchases
        const cp = cruisePurchases.map((cp) => ({
          ...cp,
          cruisePurchaseDetails: cp.isGroupedPrice
            ? [{ ...cp.cruisePurchaseDetails[0], guestId: '', guestName: '' }]
            : cp.cruisePurchaseDetails,
        }));

        const needApproval =
          values.transactionAt?.getTime() !== tempTransactionAt?.getTime();

        const result = {
          ...rest,
          total,
          needApproval,
          cruisePurchases: cp,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('cruise-purchases');
  const { onVoid, isActive, voidMutate, voidPermission } = useVoidAction(
    'cruise-purchases',
    cruisePurchase,
  );
  const { revertVoidButton } = useRevertVoidAction(
    'cruise-purchases',
    cruisePurchase,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'cruise-purchases',
    cruisePurchase,
  );
  const { resolverButton } = useResolveApproval(
    'cruise-purchases',
    cruisePurchase,
  );
  const { requestApprovalButton } = useApprovalAction(
    'cruise-purchases',
    cruisePurchase,
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (cruisePurchase) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
          {revertVoidButton}
        </div>
      );
    }
    return undefined;
  }, [
    cruisePurchase,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    revertVoidButton,
  ]);

  const onRelatedOrderAfterChange = React.useCallback(
    (value: CruisePurchaseModel) => {
      methods.setValue('parentRefId', value.refId);
      methods.setValue('agentId', value.agent?.id || '');
      methods.setValue('invoiceId', value.sale?.id || '');
      methods.setValue('tourId', value.tour?.id || '');
      methods.setValue('type', value.type || 'sales');
      methods.setValue('paymentChannelId', value.paymentChannel?.id || '');
    },
    [methods],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!cruisePurchase}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        cruisePurchase &&
        `${t('common:cruise_purchase')} - ${cruisePurchase.number}`
      }
      keyName={asPath}
    >
      <FormContent>
        {renderError}
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={cruisePurchase}
              noSubmit
              onDelete={onVoid}
              noCancel
              noDelete={editable || !voidPermission}
              title={
                cruisePurchase ? (
                  <TitleStatus
                    type="purchase"
                    status={cruisePurchase.status}
                    title={`${t('common:cruise_purchase')} - ${
                      cruisePurchase.number
                    }`}
                  />
                ) : (
                  t('common:add_extra', {
                    extra: t('common:cruise_purchase'),
                  })
                )
              }
              isDeleteLoading={voidMutate.isLoading}
              deleteText={t('common:void')}
              noEdit={!isActive || !permission.update}
              onRenderRightComponent={onRenderRightComponent}
            />
          )}
        </FormState>
        <div>
          {cruisePurchase &&
            !!(cruisePurchase?.releasePurchases || []).length && (
              <SalePurchaseRefundTable data={cruisePurchase.releasePurchases} />
            )}
          {cruisePurchase && (
            <SaleRelatedTable
              data={cruisePurchase.sale}
              navigation={{
                sale: 'CruiseSalesView',
                purchase: 'CruisePurchasesView',
              }}
            />
          )}
          {cruisePurchase &&
            (!!cruisePurchase.parentPurchase ||
              !!(cruisePurchase.childrenPurchases || []).length) && (
              <ParentOrderRelatedTable
                data={cruisePurchase}
                navigation="CruisePurchasesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <CruisePurchaseSelectInput
                label={t('common:related_order')}
                placeholder={t('common:choose_order')}
                name="parentRefId"
                onAfterDetailChange={onRelatedOrderAfterChange}
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketOrders:group_code')}
                placeholder={t('ticketOrders:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <PurchasesDetailCard cruisePurchase={cruisePurchase} />
          <CruiseSection />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <OtherPriceComponent
            type="cruise-purchases"
            data={props.cruisePurchase}
          />
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryCruisePurchase cruisePurchase={cruisePurchase} />
    </Form>
  );
}
